// useLayoutFixer.js - Hook to automatically fix widget layouts for mobile/tablet
import { useEffect, useRef, useState, useCallback } from "react";
import { hasLayoutOverlaps, compactAndFixLayout } from "../utils/layoutUtils";

/**
 * Hook that automatically fixes widget layouts when viewing on mobile/tablet
 * This ensures widgets are properly arranged without overlaps or gaps
 */
export const useLayoutFixer = ({
  widgets,
  activeLayout,
  handleWidgetUpdate,
  isEditing = false,
}) => {
  const lastFixedLayout = useRef(null);
  const fixedLayoutsRef = useRef(new Set());
  const [localFixedWidgets, setLocalFixedWidgets] = useState(null);

  // Function to check if we need to apply fixes
  const needsLayoutFix = useCallback((widgetList, layout) => {
    if (!widgetList || widgetList.length === 0) return false;
    if (layout !== "mobile" && layout !== "tablet") return false;

    // Check for overlaps or layout issues
    const hasOverlaps = hasLayoutOverlaps(widgetList, layout);

    return hasOverlaps;
  }, []);

  useEffect(() => {
    if (!widgets || widgets.length === 0) {
      setLocalFixedWidgets(null);
      return;
    }

    // Create a signature for the current layout state
    const layoutSignature = widgets
      .map(
        (w) =>
          `${w.id}-${w.layout?.[activeLayout]?.position || 0}-${w.layout?.[activeLayout]?.startColumn || 0}-${w.layout?.[activeLayout]?.columns || 4}`
      )
      .join(",");

    // Skip if we've already fixed this exact layout
    if (lastFixedLayout.current === layoutSignature) {
      return;
    }

    // Check if fixes are needed
    if (needsLayoutFix(widgets, activeLayout)) {
      console.log(`[Layout Fixer] Fixing ${activeLayout} layout issues`);

      // Apply layout fixes for positioning
      const fixedWidgets = compactAndFixLayout(widgets, activeLayout);

      // Update the layout signature
      lastFixedLayout.current = layoutSignature;

      // For editing mode with a valid handler, try to save the changes
      if (
        isEditing &&
        handleWidgetUpdate &&
        typeof handleWidgetUpdate === "function"
      ) {
        const testResult = handleWidgetUpdate(null, { type: "TEST" });
        if (testResult !== false) {
          // Handler seems functional, use it
          setTimeout(() => {
            handleWidgetUpdate(null, {
              type: "UPDATE_WIDGET",
              widgets: fixedWidgets,
              triggerAutosave: false,
            });
          }, 150);
          setLocalFixedWidgets(null);
          return;
        }
      }

      // For viewing mode or non-functional handlers, use local state
      setLocalFixedWidgets(fixedWidgets);
    } else {
      // No fixes needed, clear local fixes
      setLocalFixedWidgets(null);
    }
  }, [widgets, activeLayout, handleWidgetUpdate, isEditing, needsLayoutFix]);

  // Reset the signature when widgets change significantly
  useEffect(() => {
    const currentWidgetIds = new Set(widgets?.map((w) => w.id) || []);
    const lastFixedIds = Array.from(fixedLayoutsRef.current);

    // If widget IDs have changed (addition/removal), reset the fixer
    const idsChanged =
      lastFixedIds.some((id) => !currentWidgetIds.has(id)) ||
      widgets?.some((w) => !fixedLayoutsRef.current.has(w.id));

    if (idsChanged) {
      lastFixedLayout.current = null;
      fixedLayoutsRef.current = currentWidgetIds;
      setLocalFixedWidgets(null);
    }
  }, [widgets]);

  return {
    fixedWidgets: localFixedWidgets,
    hasAppliedFixes: !!localFixedWidgets,
  };
};

/**
 * Utility function to manually trigger layout fixes
 * Useful for calling after widget additions or major changes
 */
export const manuallyFixLayout = (
  widgets,
  targetLayout,
  handleWidgetUpdate
) => {
  if (!widgets || widgets.length === 0) return;

  const hasIssues =
    hasLayoutOverlaps(widgets, targetLayout) ||
    (targetLayout === "mobile" &&
      widgets.some((widget) => {
        const layout = widget.layout?.mobile;
        return layout && (layout.startColumn !== 0 || layout.columns !== 4);
      }));

  if (hasIssues) {
    const fixedWidgets = compactAndFixLayout(widgets, targetLayout);

    if (handleWidgetUpdate && fixedWidgets !== widgets) {
      handleWidgetUpdate(null, {
        type: "UPDATE_WIDGET",
        widgets: fixedWidgets,
        triggerAutosave: true,
      });
    }

    return fixedWidgets;
  }

  return widgets;
};
